class IngredientsGridReveal extends HTMLElement {
  #controller = null;
  #grid = null;
  #revealButton = null;
  #revealButtonLabel = null;
  #animationFrame = null;
  #scrollTimeout = null;

  connectedCallback() {
    this.#controller = new AbortController();
    this.#grid = this.querySelector('.ingredients-grid__grid');
    this.#revealButton = this.querySelector('.ingredients-grid__action-button--reveal');
    this.#revealButtonLabel = this.#revealButton?.querySelector('.button__label') ?? null;

    this.#revealButton?.addEventListener('click', () => this.#toggle(), {
      signal: this.#controller.signal,
    });

    this.#grid?.addEventListener('transitionend', (event) => this.#onGridTransitionEnd(event), {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
    }

    if (this.#scrollTimeout) {
      clearTimeout(this.#scrollTimeout);
    }

    this.#controller?.abort();
    this.#controller = null;
    this.#grid = null;
    this.#revealButton = null;
    this.#revealButtonLabel = null;
    this.#animationFrame = null;
    this.#scrollTimeout = null;
  }

  #toggle() {
    if (this.hasAttribute('data-animating')) {
      return;
    }

    const revealed = this.getAttribute('data-revealed') === 'true';
    const next = !revealed;

    if (!this.#grid || this.#prefersReducedMotion()) {
      this.#setRevealState(next);

      if (!next) {
        this.#scheduleScrollToRevealTarget();
      }

      return;
    }

    this.#animateRevealState(next);

    if (!next) {
      this.#scheduleScrollToRevealTarget();
    }
  }

  #setRevealState(revealed) {
    this.#setRevealAttribute(revealed);
    this.#setButtonState(revealed);
  }

  #setRevealAttribute(revealed) {
    this.setAttribute('data-revealed', String(revealed));
  }

  #setButtonState(revealed) {
    this.#revealButton?.setAttribute('aria-expanded', String(revealed));
    this.#revealButton?.focus({ preventScroll: true });

    if (this.#revealButtonLabel && this.#revealButton) {
      this.#revealButtonLabel.textContent = revealed
        ? this.#revealButton.dataset.hideLabel
        : this.#revealButton.dataset.showLabel;
    }
  }

  #animateRevealState(revealed) {
    if (!this.#grid) {
      this.#setRevealState(revealed);
      return;
    }

    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
    }

    const startHeight = this.#grid.getBoundingClientRect().height;

    this.setAttribute('data-animating', 'true');
    this.#grid.style.height = `${startHeight}px`;

    this.#animationFrame = requestAnimationFrame(() => {
      this.#setRevealState(revealed);

      const endHeight = this.#grid?.scrollHeight ?? startHeight;

      this.#setGridHeight(startHeight, endHeight);
    });
  }

  #setGridHeight(startHeight, endHeight) {
    if (!this.#grid) {
      return;
    }

    this.#grid.style.height = `${endHeight}px`;
    this.#animationFrame = null;

    if (Math.abs(startHeight - endHeight) < 1) {
      this.#resetGridAnimation();
    }
  }

  #onGridTransitionEnd(event) {
    if (event.target !== this.#grid || event.propertyName !== 'height') {
      return;
    }

    this.#resetGridAnimation();
  }

  #resetGridAnimation() {
    this.removeAttribute('data-animating');

    if (this.#grid) {
      this.#grid.style.height = '';
    }
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #scheduleScrollToRevealTarget() {
    if (this.#scrollTimeout) {
      clearTimeout(this.#scrollTimeout);
    }

    const delay = this.#prefersReducedMotion() ? 0 : 120;

    this.#scrollTimeout = setTimeout(() => {
      requestAnimationFrame(() => this.#scrollToRevealTarget());
      this.#scrollTimeout = null;
    }, delay);
  }

  #scrollToRevealTarget() {
    const target = this.closest('.ingredients-grid') ?? this;

    target.scrollIntoView({
      block: 'start',
      behavior: this.#prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('ingredients-grid-reveal')) {
  customElements.define('ingredients-grid-reveal', IngredientsGridReveal);
}
